import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../services/api';
import DealSlipViewModal from '../components/DealSlipViewModal';
import { SLIP_DEAL_TYPE, toSlipTransaction } from '../utils/dealSlip';
import './DailyCashflowBlotterPage.css';

/**
 * Daily Cashflow Blotter - one day's cash movement as a statement:
 *
 *   opening balance (keyed in by hand)
 *     + / - maturities and deals settling that day
 *     - bank charges
 *   = closing balance
 *
 * Deals still in the approval chain are shown pulsing and are NOT counted in
 * the closing balance. That is deliberate: a closing figure that quietly
 * absorbed half-approved deals would be wrong in a way nobody could see.
 */

const ENDPOINT = `${API_BASE_URL}/daily-cashflow-blotter`;

const ymd = (v) => (v ? String(v).slice(0, 10) : '—');

const money = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Negative figures read as (1,234.56), the convention used elsewhere here. */
const signed = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n) || 0;
  return v < 0 ? `(${money(Math.abs(v))})` : money(v);
};

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// entry_date left blank means "the day the blotter is showing".
const EMPTY_CHARGE = { entry_date: '', amount: '', description: '', bank_account_code: '', reference_no: '' };

// A coupon receipt is neither a maturity nor a new deal - labelling it "New Deal"
// would read as a trade that never happened.
const EVENT_LABELS = {
  maturity: 'Maturity',
  settlement: 'New Deal',
  coupon: 'Coupon'
};

// Cashflow row deal_type -> daily deal blotter deal type, for the deal slip lookup.
const SLIP_LOOKUP_TYPE = {
  gsec: 'GSEC',
  tbill: 'TBILL',
  buyback: 'BUYBACK',
  money_market: 'MONEY_MARKET',
  repo: 'REPO'
};

/** Deal type to open in the deal slip, or null for lines with no single deal (coupons). */
const slipTypeFor = (r) => (r.event_type === 'coupon' ? null : SLIP_LOOKUP_TYPE[r.deal_type] || null);

const rowKey = (r, prefix) => `${prefix}-${r.deal_type}-${r.event_type}-${r.id}-${r.cash_flow}`;

export default function DailyCashflowBlotterPage() {
  const [date, setDate] = useState(todayYmd());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [openingInput, setOpeningInput] = useState('');
  const [savingOpening, setSavingOpening] = useState(false);

  const [charge, setCharge] = useState(EMPTY_CHARGE);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [savingCharge, setSavingCharge] = useState(false);
  const [busyChargeId, setBusyChargeId] = useState(null);

  // Deal slip for the row being viewed: { dealType, transaction }.
  const [viewing, setViewing] = useState(null);
  const [viewLoadingKey, setViewLoadingKey] = useState('');

  const openView = async (r, key) => {
    const dealType = slipTypeFor(r);
    if (!dealType) return;
    setViewLoadingKey(key);
    setError('');
    try {
      const dealNumber = r.deal_number || (dealType === 'REPO' ? `REPO-${r.id}` : '');
      const res = await axios.get(`${API_BASE_URL}/daily-deal-blotter/deal`, {
        params: { dealType, dealNumber },
        headers: getAuthHeaders()
      });
      setViewing({
        dealType: SLIP_DEAL_TYPE[dealType],
        transaction: toSlipTransaction(dealType, res.data.data, r.counterparty)
      });
    } catch (err) {
      setError(err.response?.data?.error || `Could not load deal ${r.deal_number}`);
    } finally {
      setViewLoadingKey('');
    }
  };

  const viewCell = (r, key) => (
    <td className="dcb-view-cell">
      {slipTypeFor(r) ? (
        <button
          type="button"
          className="dcb-btn dcb-btn--sm dcb-btn--view"
          onClick={() => openView(r, key)}
          disabled={viewLoadingKey === key}
        >
          {viewLoadingKey === key ? 'Loading…' : 'View'}
        </button>
      ) : (
        <span className="dcb-muted">—</span>
      )}
    </td>
  );

  const load = useCallback(async (ymd) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(ENDPOINT, { params: { date: ymd }, headers: getAuthHeaders() });
      setData(res.data);
      setOpeningInput(
        res.data.opening_balance === null || res.data.opening_balance === undefined
          ? ''
          : String(res.data.opening_balance)
      );
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load the blotter');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(date);
  }, [date, load]);

  useEffect(() => {
    axios
      .get(`${ENDPOINT}/bank-accounts`, { headers: getAuthHeaders() })
      .then((res) => setBankAccounts(res.data.accounts || []))
      .catch(() => setBankAccounts([]));
  }, []);

  const saveOpening = async () => {
    const amount = Number(openingInput);
    if (!Number.isFinite(amount)) {
      setError('Opening balance must be a number');
      return;
    }
    setSavingOpening(true);
    setError('');
    try {
      await axios.put(
        `${ENDPOINT}/opening-balance`,
        { date, opening_balance: amount },
        { headers: getAuthHeaders() }
      );
      await load(date);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the opening balance');
    } finally {
      setSavingOpening(false);
    }
  };

  const addCharge = async () => {
    const chargeDate = charge.entry_date || date;
    if (!chargeDate || !charge.amount || !charge.description.trim() || !charge.bank_account_code) {
      setError('A bank charge needs a date, an amount, a description and a bank account');
      return;
    }
    setSavingCharge(true);
    setError('');
    try {
      await axios.post(
        `${ENDPOINT}/charges`,
        {
          entry_date: chargeDate,
          amount: Number(charge.amount),
          description: charge.description.trim(),
          bank_account_code: charge.bank_account_code,
          reference_no: charge.reference_no || null
        },
        { headers: getAuthHeaders() }
      );
      setCharge(EMPTY_CHARGE);
      // A charge for another day lands on that day's blotter, so go there to show it.
      if (chargeDate !== date) setDate(chargeDate);
      else await load(date);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the charge');
    } finally {
      setSavingCharge(false);
    }
  };

  const actOnCharge = async (id, action) => {
    setBusyChargeId(id);
    setError('');
    try {
      if (action === 'delete') {
        await axios.delete(`${ENDPOINT}/charges/${id}`, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${ENDPOINT}/charges/${id}/${action}`, {}, { headers: getAuthHeaders() });
      }
      await load(date);
    } catch (err) {
      setError(err.response?.data?.message || `Could not ${action} the charge`);
    } finally {
      setBusyChargeId(null);
    }
  };

  const totals = data?.totals || {};
  const pendingSummary = data?.pending_summary || {};
  const openingKnown = data?.opening_balance !== null && data?.opening_balance !== undefined;

  // Depend on the counts, not on pendingSummary - that object is rebuilt on
  // every render, so memoising against it would never actually memoise.
  const pendingDeals = pendingSummary.deal_count || 0;
  const pendingCharges = pendingSummary.charges_awaiting_approval || 0;

  const pendingNote = useMemo(() => {
    if (!pendingDeals && !pendingCharges) return null;
    const bits = [];
    if (pendingDeals) bits.push(`${pendingDeals} deal${pendingDeals === 1 ? '' : 's'}`);
    if (pendingCharges) bits.push(`${pendingCharges} bank charge${pendingCharges === 1 ? '' : 's'}`);
    return bits.join(' and ');
  }, [pendingDeals, pendingCharges]);

  return (
    <div className="dcb-page">
      <header className="dcb-header">
        <div>
          <h1>Daily Cashflow Blotter</h1>
          <p className="dcb-sub">
            Cash movement for one value date, across all banks combined.
          </p>
          {data?.viewer?.role && (
            <p className="dcb-role">
              Signed in as <strong>{data.viewer.username}</strong> ({data.viewer.role})
              {data.viewer.can_approve
                ? ' — can give final approval on bank charges'
                : data.viewer.can_verify
                  ? ' — can verify bank charges'
                  : ' — view only for bank charge approval'}
            </p>
          )}
        </div>
        <div className="dcb-controls">
          <label className="dcb-field">
            <span>Value Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <button type="button" className="dcb-btn" onClick={() => load(date)} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </header>

      {error && <div className="dcb-alert dcb-alert--error">{error}</div>}

      {pendingNote && (
        <div className="dcb-alert dcb-alert--pending">
          <span className="dcb-spinner" aria-hidden="true" />
          <div>
            <strong>{pendingNote} still awaiting approval.</strong>
            <div>
              The closing balance below counts approved items only. It is not final until these
              are approved.
            </div>
          </div>
        </div>
      )}

      <section className="dcb-statement">
        {/* ---------------------------------------------------- opening */}
        <div className="dcb-band dcb-band--opening">
          <div className="dcb-band-label">Opening Balance</div>
          <div className="dcb-opening-entry">
            <input
              type="number"
              step="0.01"
              value={openingInput}
              placeholder="Enter the opening bank balance"
              onChange={(e) => setOpeningInput(e.target.value)}
              className="dcb-opening-input"
            />
            <button type="button" className="dcb-btn" onClick={saveOpening} disabled={savingOpening}>
              {savingOpening ? 'Saving…' : 'Save'}
            </button>
          </div>
          <div className="dcb-band-amount">{openingKnown ? money(data.opening_balance) : '—'}</div>
        </div>
        {!openingKnown && !loading && (
          <div className="dcb-hint">
            Enter the opening balance to produce a closing balance.
          </div>
        )}

        {/* ---------------------------------------------------- deals */}
        <table className="dcb-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Instrument</th>
              <th>Description</th>
              <th>Deal No.</th>
              <th>Reference Deal</th>
              <th>Trade Date</th>
              <th>Status</th>
              <th className="dcb-num">Amount</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={rowKey(r, 's')}>
                <td>
                  <span className={`dcb-chip dcb-chip--${r.event_type}`}>
                    {EVENT_LABELS[r.event_type] || 'New Deal'}
                  </span>
                </td>
                <td>{r.instrument}</td>
                <td className="dcb-desc">{r.description}</td>
                <td className="dcb-mono">{r.deal_number}</td>
                <td className="dcb-mono">{r.reference_deal_number || '—'}</td>
                <td className="dcb-mono">{ymd(r.trade_date)}</td>
                <td>
                  <span className="dcb-status">{r.status}</span>
                </td>
                <td className={`dcb-num ${r.settlement_value < 0 ? 'dcb-neg' : 'dcb-pos'}`}>
                  {signed(r.settlement_value)}
                </td>
                {viewCell(r, rowKey(r, 's'))}
              </tr>
            ))}

            {(data?.pending || []).map((r) => (
              <tr
                key={rowKey(r, 'p')}
                className="dcb-row--pending"
                title="Awaiting approval — not counted in the closing balance"
              >
                <td>
                  <span className="dcb-chip dcb-chip--pending">
                    <span className="dcb-dot" />
                    Pending
                  </span>
                </td>
                <td>{r.instrument}</td>
                <td className="dcb-desc">{r.description}</td>
                <td className="dcb-mono">{r.deal_number}</td>
                <td className="dcb-mono">{r.reference_deal_number || '—'}</td>
                <td className="dcb-mono">{ymd(r.trade_date)}</td>
                <td>
                  <span className="dcb-status dcb-status--pending">{r.status}</span>
                </td>
                <td className="dcb-num dcb-num--muted">{signed(r.settlement_value)}</td>
                {viewCell(r, rowKey(r, 'p'))}
              </tr>
            ))}

            {!loading && !(data?.rows || []).length && !(data?.pending || []).length && (
              <tr>
                <td colSpan={9} className="dcb-empty">
                  No deals settling or maturing on this value date.
                </td>
              </tr>
            )}

            {loading && (
              <tr>
                <td colSpan={9} className="dcb-empty">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={7}>Inflows</td>
              <td className="dcb-num dcb-pos">{money(totals.inflows)}</td>
              <td />
            </tr>
            <tr>
              <td colSpan={7}>Outflows</td>
              <td className="dcb-num dcb-neg">{signed(totals.outflows)}</td>
              <td />
            </tr>
          </tfoot>
        </table>

        {/* ---------------------------------------------------- charges */}
        <h2 className="dcb-section-title">Bank Charges</h2>
        <table className="dcb-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Bank Account</th>
              <th>Reference</th>
              <th>Status</th>
              <th className="dcb-num">Amount</th>
              <th className="dcb-num">Action</th>
            </tr>
          </thead>
          <tbody>
            {(data?.charges || []).map((c) => (
              <tr key={c.id} className={c.posted ? '' : 'dcb-row--unposted'}>
                <td className="dcb-mono">{ymd(c.entry_date)}</td>
                <td className="dcb-desc">
                  {c.description}
                  {(c.verified_by || c.approved_by) && (
                    <div className="dcb-trail">
                      {c.verified_by && <>verified by {c.verified_by}</>}
                      {c.verified_by && c.approved_by && ' · '}
                      {c.approved_by && <>approved by {c.approved_by}</>}
                    </div>
                  )}
                </td>
                <td>{c.bank_name}</td>
                <td className="dcb-mono">{c.reference_no || '—'}</td>
                <td>
                  {c.posted ? (
                    <span className="dcb-status dcb-status--ok">{c.level_label}</span>
                  ) : (
                    <span className="dcb-status dcb-status--pending">
                      <span className="dcb-dot" />
                      {c.level_label}
                    </span>
                  )}
                  {!c.posted && (
                    <div className="dcb-level">
                      {c.status === 'pending_verification' ? 'Level 1 of 2' : 'Level 2 of 2'}
                    </div>
                  )}
                </td>
                <td className="dcb-num dcb-neg">{signed(-Math.abs(c.amount))}</td>
                <td className="dcb-num">
                  {c.posted && <span className="dcb-muted">—</span>}

                  {c.can_verify && (
                    <button
                      type="button"
                      className="dcb-btn dcb-btn--sm"
                      disabled={busyChargeId === c.id}
                      onClick={() => actOnCharge(c.id, 'verify')}
                    >
                      Verify
                    </button>
                  )}
                  {c.can_approve && (
                    <button
                      type="button"
                      className="dcb-btn dcb-btn--sm"
                      disabled={busyChargeId === c.id}
                      onClick={() => actOnCharge(c.id, 'approve')}
                    >
                      Approve
                    </button>
                  )}
                  {!c.posted && (c.can_verify || c.can_approve) && (
                    <button
                      type="button"
                      className="dcb-btn dcb-btn--sm dcb-btn--ghost"
                      disabled={busyChargeId === c.id}
                      onClick={() => actOnCharge(c.id, 'reject')}
                    >
                      Reject
                    </button>
                  )}
                  {c.can_delete && !c.can_verify && (
                    <button
                      type="button"
                      className="dcb-btn dcb-btn--sm dcb-btn--ghost"
                      disabled={busyChargeId === c.id}
                      onClick={() => actOnCharge(c.id, 'delete')}
                    >
                      Delete
                    </button>
                  )}
                  {/* Nothing actionable: say whose turn it is rather than
                      showing a dead button or an unexplained blank. */}
                  {!c.posted && !c.can_verify && !c.can_approve && !c.can_delete && (
                    <span className="dcb-muted dcb-waiting">
                      with {c.status === 'pending_verification' ? 'BO verifier' : 'BO final'}
                    </span>
                  )}
                </td>
              </tr>
            ))}

            {/* Manual entry row */}
            <tr className="dcb-row--entry">
              <td>
                <input
                  type="date"
                  aria-label="Charge date"
                  value={charge.entry_date || date}
                  onChange={(e) => setCharge({ ...charge, entry_date: e.target.value })}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="e.g. RTGS transfer fees"
                  value={charge.description}
                  onChange={(e) => setCharge({ ...charge, description: e.target.value })}
                />
              </td>
              <td>
                <select
                  value={charge.bank_account_code}
                  onChange={(e) => setCharge({ ...charge, bank_account_code: e.target.value })}
                >
                  <option value="">Select bank…</option>
                  {bankAccounts.map((a) => (
                    <option key={a.account_code} value={a.account_code}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Ref."
                  value={charge.reference_no}
                  onChange={(e) => setCharge({ ...charge, reference_no: e.target.value })}
                />
              </td>
              <td className="dcb-muted">Not yet saved</td>
              <td className="dcb-num">
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  className="dcb-num-input"
                  value={charge.amount}
                  onChange={(e) => setCharge({ ...charge, amount: e.target.value })}
                />
              </td>
              <td className="dcb-num">
                <button type="button" className="dcb-btn dcb-btn--sm" onClick={addCharge} disabled={savingCharge}>
                  {savingCharge ? 'Adding…' : 'Add'}
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>Total bank charges</td>
              <td className="dcb-num dcb-neg">{signed(-Math.abs(totals.bank_charges || 0))}</td>
              <td />
            </tr>
          </tfoot>
        </table>

        {/* ---------------------------------------------------- closing */}
        <div className="dcb-band dcb-band--closing">
          <div className="dcb-band-label">
            Closing Balance
            <span className="dcb-band-note">Opening + inflows − outflows − charges</span>
          </div>
          <div className="dcb-band-amount">
            {totals.closing_balance === null || totals.closing_balance === undefined
              ? '—'
              : money(totals.closing_balance)}
          </div>
        </div>

        {pendingDeals > 0 && openingKnown && (
          <div className="dcb-provisional">
            If the {pendingDeals} pending deal{pendingDeals === 1 ? '' : 's'}{' '}
            {pendingDeals === 1 ? 'is' : 'are'} approved,
            the closing balance becomes <strong>{money(totals.provisional_closing)}</strong>.
          </div>
        )}
      </section>

      {viewing && (
        <DealSlipViewModal
          dealType={viewing.dealType}
          transaction={viewing.transaction}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
